import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';

export default function ArticleGuide() {
  const fadeRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-6');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -50px 0px' }
    );

    fadeRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Navbar />

      {/* Article Hero */}
      <section className="relative w-full pt-24 pb-12 md:pt-28 md:pb-16 bg-background-100/50">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
              Home
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-400 whitespace-nowrap">Articles</span>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-primary-600 font-medium truncate">Complete Sugargoo Spreadsheet Guide</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary-100 rounded-full mb-4">
            <i className="ri-book-open-line text-sm text-primary-600"></i>
            <span className="text-xs text-primary-700 font-medium">COMPREHENSIVE GUIDE</span>
          </div>

          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
            The Complete Sugargoo Spreadsheet Guide: How to Find, Organize and Shop Smarter in 2026
          </h1>

          <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl mb-6">
            Everything you need to know about using a Sugargoo Spreadsheet to streamline your
            international shopping — from finding the best product links to organizing categories,
            comparing sellers and making confident purchasing decisions.
          </p>

          <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i> Updated August 4, 2026
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i> 12 min read
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i> By SugargooSheet Study Team
            </span>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="w-full px-6 md:px-10 py-10 md:py-14 bg-background-50">
        <div className="max-w-4xl mx-auto">
          <div className="prose-custom space-y-8">

            {/* Introduction */}
            <section ref={(el) => { fadeRefs.current[0] = el; }} className="opacity-0 translate-y-6 transition-all duration-700 ease-out">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                Introduction: Why a Sugargoo Spreadsheet Matters
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                International shopping through agent platforms has become one of the most popular ways
                for buyers around the world to access products that would otherwise be unavailable or
                prohibitively expensive. However, the sheer volume of products, sellers and listings
                can be overwhelming — especially for newcomers. This is where a well-structured
                <strong> Sugargoo Spreadsheet</strong> becomes an indispensable tool.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                A <strong>Sugargoo Spreadsheet</strong> is more than just a list of links. It is a
                curated, categorized and community-verified database that organizes products into
                logical groups, includes pricing information, seller ratings and quality control
                notes. For anyone serious about getting the best value from their international
                shopping experience, using a spreadsheet can mean the difference between a frustrating
                search and a seamless discovery process.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                In this guide, we will walk through everything from the basics of what a
                <strong> Sugargoo Spreadsheet</strong> actually contains, to advanced strategies
                for using the data to find hidden gems and avoid common pitfalls. Whether you are
                a complete beginner or an experienced shopper looking to level up your approach,
                this comprehensive resource will help you navigate the world of agent-based shopping
                with confidence.
              </p>
            </section>

            {/* What is a Sugargoo Spreadsheet */}
            <section ref={(el) => { fadeRefs.current[1] = el; }} className="opacity-0 translate-y-6 transition-all duration-700 ease-out">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                What Exactly Is a Sugargoo Spreadsheet?
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                At its core, a <strong>Sugargoo Spreadsheet</strong> is a structured document — typically
                built in Google Sheets or a similar platform — that compiles product listings, links,
                prices, seller information and community feedback into an easily navigable format.
                Unlike browsing through endless pages of search results, a spreadsheet allows you to
                filter, sort and compare products side by side.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                The most effective spreadsheets include the following data points for each product entry:
              </p>
              <ul className="space-y-2 pl-5">
                {[
                  'Product name and direct purchase link',
                  'Category classification (shoes, clothing, accessories, etc.)',
                  'Price range in the original currency and estimated USD/EUR conversion',
                  'Seller rating based on transaction history and community feedback',
                  'Available sizes and color options',
                  'Quality control status — whether QC photos have been reviewed',
                  'Shipping weight estimates for cost calculation',
                  'Last updated date to ensure freshness of the information',
                ].map((item, i) => (
                  <li key={i} className="text-sm md:text-base text-foreground-700 leading-relaxed flex items-start gap-2">
                    <i className="ri-check-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mt-4">
                Having all of this information in one place eliminates the need to jump between
                multiple browser tabs, manually compare prices or dig through comments sections
                for quality insights. It is the difference between organized research and chaotic browsing.
              </p>
            </section>

            {/* Key Benefits */}
            <section ref={(el) => { fadeRefs.current[2] = el; }} className="opacity-0 translate-y-6 transition-all duration-700 ease-out">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                Five Key Benefits of Using a Sugargoo Product Spreadsheet
              </h2>

              <div className="space-y-5">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading font-bold text-primary-600 text-lg">1</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">Massive Time Savings</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Searching manually for products across platforms can consume hours of your time.
                      A <strong>Sugargoo Spreadsheet</strong> consolidates the best finds into one
                      location, allowing you to browse through hundreds of curated products in the
                      time it would normally take to evaluate just a handful of individual listings.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading font-bold text-accent-600 text-lg">2</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">Reduced Risk of Bad Purchases</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      One of the biggest concerns for international shoppers is receiving a product
                      that does not match its description. Spreadsheets that include QC verification
                      notes and community reviews help you avoid unreliable sellers and low-quality
                      items before you commit any money.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading font-bold text-secondary-600 text-lg">3</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">Better Price Discovery</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      Prices for the same product can vary significantly between sellers. A
                      well-maintained spreadsheet tracks these variations, helping you identify
                      the best deals and avoid overpaying. Some spreadsheets even include
                      historical price data so you can see pricing trends over time.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading font-bold text-primary-600 text-lg">4</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">Discover Hidden Gems</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      The curated nature of a <strong>Sugargoo Spreadsheet</strong> means you will
                      often come across products you would never have found through regular searching.
                      From unique streetwear pieces to niche accessories, the community-driven
                      discovery process surfaces interesting items that algorithms might overlook.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <span className="font-heading font-bold text-accent-600 text-lg">5</span>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1.5">Beginner-Friendly Entry Point</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      If you are new to agent-based shopping, the learning curve can feel steep.
                      A spreadsheet provides a structured, guided experience that makes the entire
                      process more approachable. Instead of figuring everything out from scratch,
                      you can follow the established patterns of experienced community members.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Category Breakdown */}
            <section ref={(el) => { fadeRefs.current[3] = el; }} className="opacity-0 translate-y-6 transition-all duration-700 ease-out">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                Understanding the 14 Product Categories
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                A comprehensive <strong>Sugargoo Spreadsheet</strong> organizes products into
                fourteen distinct categories, each serving a specific segment of the shopping
                experience. Understanding these categories helps you navigate the spreadsheet
                efficiently and find exactly what you are looking for.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                {[
                  { name: 'Shoes', desc: 'From luxury sneakers to budget-friendly trainers, the footwear category covers every style and price point.' },
                  { name: 'Hoodies / Sweaters', desc: 'Cozy streetwear staples including oversized hoodies, premium sweaters and seasonal knitwear.' },
                  { name: 'T-Shirts', desc: 'Graphic tees, designer logos and everyday basics in a wide range of fits and fabrics.' },
                  { name: 'Jackets', desc: 'Bombers, varsity jackets, winter coats and lightweight outerwear for layering in any climate.' },
                  { name: 'Pants / Shorts', desc: 'Cargo pants, denim jeans, joggers, tailored trousers and summer shorts for any occasion.' },
                  { name: 'Headwear', desc: 'Snapbacks, beanies, bucket hats and caps in various styles to complete your look.' },
                  { name: 'Sets', desc: 'Matching tracksuits and coordinated two-piece outfits for a put-together appearance.' },
                  { name: 'Underwear / Underpants', desc: 'Quality basics including boxers, briefs and essential base layers from trusted suppliers.' },
                  { name: 'Jersey', desc: 'Sports jerseys including football kits, basketball uniforms and fan merchandise.' },
                  { name: 'Accessories', desc: 'Watches, jewelry, sunglasses and other statement pieces that add personality to any outfit.' },
                  { name: 'Bags', desc: 'Backpacks, messenger bags, totes and luxury-inspired handbags for function and style.' },
                  { name: 'Belts', desc: 'Designer-inspired belts in various materials, buckle styles and sizes.' },
                  { name: 'Perfumes', desc: 'Fragrances inspired by popular designer scents at accessible price points.' },
                  { name: 'Other Stuff', desc: 'Electronics, gadgets, home goods and miscellaneous finds that do not fit other categories.' },
                ].map((cat, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-background-100/60 border border-background-200/40">
                    <div className="w-8 h-8 rounded-lg bg-background-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-foreground-600">{i + 1}</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900">{cat.name}</h4>
                      <p className="text-xs text-foreground-600 leading-relaxed mt-0.5">{cat.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* How to Use */}
            <section ref={(el) => { fadeRefs.current[4] = el; }} className="opacity-0 translate-y-6 transition-all duration-700 ease-out">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                How to Use a Sugargoo Spreadsheet Effectively
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Getting the most value from a <strong>Sugargoo Spreadsheet</strong> requires a
                systematic approach. Here is a step-by-step workflow that experienced shoppers
                recommend:
              </p>

              <div className="space-y-4">
                <div className="flex gap-4 p-4 rounded-xl bg-background-100/80 border border-background-200/50">
                  <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-background-50">1</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Start with Your Priority Category</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      Do not try to browse everything at once. Pick the category that matters most
                      to you — whether it is <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">shoes</Link> for a
                      new sneaker release or <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">hoodies</Link> for
                      seasonal updates — and focus your initial exploration there.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-4 rounded-xl bg-background-100/80 border border-background-200/50">
                  <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-background-50">2</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Check QC Status and Seller Ratings</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      Before clicking any product link, review the quality control notes and seller
                      rating columns. Products with verified QC and high seller ratings should be
                      your priority targets.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-4 rounded-xl bg-background-100/80 border border-background-200/50">
                  <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-background-50">3</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Compare Prices Across Sellers</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      Similar products often appear from multiple sellers at different price points.
                      Use the spreadsheet to identify the best combination of price, quality and
                      seller reliability.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-4 rounded-xl bg-background-100/80 border border-background-200/50">
                  <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-background-50">4</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Note Shipping Weight Estimates</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      International shipping costs are heavily influenced by package weight. Products
                      with lower estimated weights can significantly reduce your total cost, especially
                      when ordering multiple items.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-4 rounded-xl bg-background-100/80 border border-background-200/50">
                  <div className="w-9 h-9 rounded-full bg-primary-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-background-50">5</span>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1">Open Links and Place Your Order</h4>
                    <p className="text-xs text-foreground-600 leading-relaxed">
                      Once you have identified your target products, use the direct links in the
                      spreadsheet to open each listing. Copy the link into your agent platform and
                      complete your order following the standard purchase flow.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Common Mistakes */}
            <section ref={(el) => { fadeRefs.current[5] = el; }} className="opacity-0 translate-y-6 transition-all duration-700 ease-out">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                Common Mistakes to Avoid
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Even with a great <strong>Sugargoo Spreadsheet</strong> at your disposal, there are
                several pitfalls that newcomers frequently encounter. Being aware of these can save
                you time, money and frustration.
              </p>

              <div className="space-y-3">
                {[
                  {
                    title: 'Ignoring the Last Updated Date',
                    desc: 'Product availability and pricing change constantly. Always check when the spreadsheet entry was last refreshed. Entries older than a few weeks may contain outdated links or prices that are no longer accurate.',
                  },
                  {
                    title: 'Skipping QC Verification',
                    desc: 'Quality control notes exist for a reason. Products without QC verification carry a higher risk of arriving with defects or not matching the listing description. Prioritize verified entries whenever possible.',
                  },
                  {
                    title: 'Buying Without Comparing Sellers',
                    desc: 'The first link you see is not always the best option. Take an extra minute to compare the same product across different sellers in the spreadsheet — the savings can be significant.',
                  },
                  {
                    title: 'Overlooking Shipping Weight',
                    desc: 'Heavy items like jackets and shoes can dramatically increase your international shipping costs. Factor in estimated weight before committing to a purchase, especially for bulk orders.',
                  },
                  {
                    title: 'Not Checking Size Availability',
                    desc: 'Many products in international spreadsheets cater to Asian sizing, which differs from US and EU standards. Always verify the size chart and available options before ordering.',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 p-4 rounded-xl bg-background-100/80 border border-background-200/50">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-accent-600 text-sm"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1">{item.title}</h4>
                      <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section ref={(el) => { fadeRefs.current[6] = el; }} className="opacity-0 translate-y-6 transition-all duration-700 ease-out">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                Frequently Asked Questions About Sugargoo Spreadsheets
              </h2>

              <div className="space-y-4">
                {[
                  {
                    q: 'Is a Sugargoo Spreadsheet free to use?',
                    a: 'Yes, most community-maintained Sugargoo Spreadsheets are freely accessible. They are created by experienced shoppers who want to share their research with the broader community. There is no charge to browse, filter or use the product links contained in a spreadsheet.',
                  },
                  {
                    q: 'How often is the Sugargoo Spreadsheet updated?',
                    a: 'Update frequency varies by spreadsheet. The best resources are refreshed weekly or even daily with new product finds, while discontinued items are removed and prices are adjusted. Always check the &ldquo;Last Updated&rdquo; field for each entry to gauge freshness.',
                  },
                  {
                    q: 'Can I trust the seller ratings in the spreadsheet?',
                    a: 'Seller ratings in a well-maintained Sugargoo Spreadsheet are based on aggregated community feedback and verified transaction histories. While no rating system is perfect, spreadsheets that include detailed QC notes and review counts tend to be more reliable than those with only basic ratings.',
                  },
                  {
                    q: 'Do I need special software to use a Sugargoo Spreadsheet?',
                    a: 'No special software is required. Most spreadsheets are built in Google Sheets and can be viewed directly in any web browser. Some advanced users may choose to make a copy for personal filtering and sorting, but this is entirely optional.',
                  },
                  {
                    q: 'What is the difference between a Sugargoo Spreadsheet and regular product search?',
                    a: 'Regular product search returns unfiltered results sorted by platform algorithms. A Sugargoo Spreadsheet is manually curated by experienced community members who have already vetted products for quality, reliability and value. It is the difference between browsing a department store and having a personal shopping assistant.',
                  },
                  {
                    q: 'How do I get started with my first order using the spreadsheet?',
                    a: 'Start by visiting our <a href="/" class="text-primary-500 hover:text-primary-600">homepage</a> and selecting a category that interests you. Browse the curated products, note the prices and QC status, then click through to the main site where you can copy product links into your agent platform and place your order.',
                  },
                ].map((faq, i) => (
                  <details key={i} className="group rounded-xl border border-background-200/70 bg-background-50 overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-100/50 transition-colors">
                      <span className="text-sm font-semibold text-foreground-900 pr-4">{faq.q}</span>
                      <i className="ri-add-line text-foreground-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-foreground-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <div className="px-5 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.a }}></p>
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Conclusion */}
            <section ref={(el) => { fadeRefs.current[7] = el; }} className="opacity-0 translate-y-6 transition-all duration-700 ease-out">
              <h2 className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 mb-4">
                Conclusion: Start Shopping Smarter Today
              </h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                A <strong>Sugargoo Spreadsheet</strong> is one of the most powerful tools available
                to international shoppers in 2026. By consolidating verified product links, seller
                ratings, QC notes and pricing data into an organized, easy-to-navigate format, it
                transforms what could be an overwhelming experience into a streamlined and enjoyable
                shopping journey.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
                Whether you are hunting for the latest sneaker drops, building a seasonal wardrobe,
                or simply curious about what the international market has to offer, starting with a
                curated spreadsheet will save you time, reduce your risk and help you discover
                products you might otherwise miss completely.
              </p>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8">
                Remember to return to our <Link to="/" className="text-primary-500 hover:text-primary-600 font-medium cursor-pointer">homepage</Link> regularly for
                updated spreadsheet resources, new category additions and the latest community-vetted
                product finds. The world of international shopping is constantly evolving, and staying
                connected to an active, well-maintained spreadsheet is your best strategy for
                staying ahead of the curve.
              </p>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 p-6 rounded-2xl bg-background-100/80 border border-background-200/70">
                <div className="text-center sm:text-left mb-3 sm:mb-0">
                  <p className="text-sm font-semibold text-foreground-900">Ready to explore the latest finds?</p>
                  <p className="text-xs text-foreground-600">Browse all categories on our main platform.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2.5 w-full sm:w-auto">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer text-center"
                  >
                    Sugargoo Spreadsheet
                    <i className="ri-arrow-right-up-line ml-1.5"></i>
                  </a>
                  <Link
                    to="/"
                    className="px-6 py-2.5 bg-background-200 text-foreground-800 rounded-full font-semibold text-sm hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer text-center"
                  >
                    Back to Home
                    <i className="ri-home-line ml-1.5"></i>
                  </Link>
                </div>
              </div>
            </section>

          </div>

          {/* Related Content Sidebar */}
          <aside className="mt-12 p-6 rounded-2xl bg-background-100/60 border border-background-200/70">
            <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-4">
              Related on SugargooSheet Study
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Link
                to="/"
                className="p-4 rounded-xl bg-background-50 border border-background-200/50 hover:border-primary-300 transition-colors cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center mb-2.5">
                  <i className="ri-grid-line text-primary-600 text-sm"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1 group-hover:text-primary-600 transition-colors">Browse All Categories</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">Explore the complete product category directory with verified links.</p>
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="p-4 rounded-xl bg-background-50 border border-background-200/50 hover:border-accent-300 transition-colors cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center mb-2.5">
                  <i className="ri-shopping-bag-3-line text-accent-600 text-sm"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1 group-hover:text-accent-600 transition-colors">Main Shopping Platform</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">Visit our main website for the full spreadsheet experience.</p>
              </a>
              <Link
                to="/"
                className="p-4 rounded-xl bg-background-50 border border-background-200/50 hover:border-secondary-300 transition-colors cursor-pointer group"
              >
                <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center mb-2.5">
                  <i className="ri-information-line text-secondary-600 text-sm"></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-1 group-hover:text-secondary-600 transition-colors">Shopping Tips</h4>
                <p className="text-xs text-foreground-500 leading-relaxed">Learn more tips and strategies for international shopping success.</p>
              </Link>
            </div>
          </aside>
        </div>
      </article>

      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'The Complete Sugargoo Spreadsheet Guide: How to Find, Organize and Shop Smarter in 2026',
            description: 'Everything you need to know about using a Sugargoo Spreadsheet to streamline your international shopping — from finding the best product links to organizing categories and comparing sellers.',
            datePublished: '2026-08-04',
            dateModified: '2026-08-04',
            author: { '@type': 'Organization', name: 'SugargooSheet Study' },
            publisher: { '@type': 'Organization', name: 'SugargooSheet Study' },
            mainEntityOfPage: { '@type': 'WebPage', '@id': import.meta.env.VITE_SITE_URL + '/article/sugargoo-spreadsheet-guide' },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: 'Is a Sugargoo Spreadsheet free to use?', acceptedAnswer: { '@type': 'Answer', text: 'Yes, most community-maintained Sugargoo Spreadsheets are freely accessible. They are created by experienced shoppers who want to share their research with the broader community.' } },
              { '@type': 'Question', name: 'How often is the Sugargoo Spreadsheet updated?', acceptedAnswer: { '@type': 'Answer', text: 'Update frequency varies by spreadsheet. The best resources are refreshed weekly or even daily with new product finds.' } },
              { '@type': 'Question', name: 'Can I trust the seller ratings in the spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'Seller ratings are based on aggregated community feedback and verified transaction histories.' } },
              { '@type': 'Question', name: 'Do I need special software to use a Sugargoo Spreadsheet?', acceptedAnswer: { '@type': 'Answer', text: 'No special software is required. Most spreadsheets are built in Google Sheets and can be viewed directly in any web browser.' } },
              { '@type': 'Question', name: 'What is the difference between a Sugargoo Spreadsheet and regular product search?', acceptedAnswer: { '@type': 'Answer', text: 'Regular product search returns unfiltered results, while a Sugargoo Spreadsheet is manually curated by experienced community members.' } },
              { '@type': 'Question', name: 'How do I get started with my first order?', acceptedAnswer: { '@type': 'Answer', text: 'Start by visiting the homepage and selecting a category. Browse curated products, then click through to the main site to place your order.' } },
            ],
          }),
        }}
      />

      <Footer />
    </>
  );
}